import React, { ReactNode, useContext, createContext, useRef } from 'react';
import { createStore, StoreApi, useStore } from 'zustand';
import { WhatsAppMessage } from '../types/chat';
import { notificationSound } from '../utils/notificationSound';

export type SocketData = {
  newMessages: number[]; // channelIds with new messages
  channelUpdates: number[]; // channelIds with updates (rename, archive, etc)
  memberChanges: number[]; // channelIds with member changes
};

export type PendingMutation = {
  type: 'edit' | 'delete' | 'send';
  timestamp: number;
  channelId: number;
};

export type MessagingZustandStore = {
  // Active channel state
  activeChannelId: number | null;
  setActiveChannelId: (id: number | null) => void;

  // Socket state for real-time updates
  socketData: SocketData;
  setSocketData: (
    socketData:
      | Partial<SocketData>
      | ((prev: SocketData) => Partial<SocketData>)
  ) => void;
  resetSocketData: () => void;
  hasNewMessages: (channelId: number) => boolean;
  hasChannelUpdate: (channelId: number) => boolean;

  // UI state
  showEmojiPicker: boolean;
  toggleEmojiPicker: () => void;
  closeEmojiPicker: () => void;

  // Draft messages (auto-save)
  drafts: Record<number, string>;
  saveDraft: (channelId: number, text: string) => void;
  getDraft: (channelId: number) => string;
  clearDraft: (channelId: number) => void;
  hasDraft: (channelId: number) => boolean;

  // Reply state
  replyingTo: Record<number, WhatsAppMessage | null>;
  setReplyingTo: (channelId: number, message: WhatsAppMessage | null) => void;
  clearReplyingTo: (channelId: number) => void;

  // Editing state
  editingMessage: WhatsAppMessage | null;
  setEditingMessage: (message: WhatsAppMessage | null) => void;
  clearEditingMessage: () => void;

  // Sounds preference
  soundsEnabled: boolean;
  toggleSounds: () => void;

  // Filter state
  showUnreadOnly: boolean;
  setShowUnreadOnly: (value: boolean) => void;
  toggleUnreadFilter: () => void;

  // Mutation tracking (prevents socket events from overwriting pending mutations)
  pendingMutations: Map<string, PendingMutation>;
  trackMutation: (messageId: number, mutation: PendingMutation) => void;
  clearMutation: (messageId: number) => void;
  isPendingMutation: (messageId: number, maxAgeMs?: number) => boolean;
};

export type MessagingStore = MessagingZustandStore;

// Module-level socketData persistence to survive store recreation
// When MyConversations unmounts/remounts, the store is destroyed/recreated
// This preserves socketData (newMessages, channelUpdates, memberChanges) across lifecycles
let __persistedSocketData: SocketData | null = null;

const createMessagingStore = () => {
  const initialSocketData = __persistedSocketData || {
    newMessages: [],
    channelUpdates: [],
    memberChanges: [],
  };

  if (__persistedSocketData) {
    console.log(
      '[MessagingStore] Creating store with persisted socketData:',
      __persistedSocketData
    );
  }

  return createStore<MessagingZustandStore>()((set, get) => ({
    // Active channel
    activeChannelId: null,
    setActiveChannelId: (id: number | null) => set({ activeChannelId: id }),

    // Socket state - restore from persisted data if available
    socketData: initialSocketData,
    setSocketData: (
      updatedSocketData:
        | Partial<SocketData>
        | ((prev: SocketData) => Partial<SocketData>)
    ) =>
      set((state) => {
        const update =
          typeof updatedSocketData === 'function'
            ? updatedSocketData(state.socketData)
            : updatedSocketData;
        const newSocketData = {
          ...state.socketData,
          ...update,
        };
        // Persist immediately to module variable so it survives store recreation
        __persistedSocketData = newSocketData;
        return {
          socketData: newSocketData,
        };
      }),
    resetSocketData: () => {
      const emptySocketData = {
        newMessages: [],
        channelUpdates: [],
        memberChanges: [],
      };
      // Persist empty state
      __persistedSocketData = emptySocketData;
      return set({
        socketData: emptySocketData,
      });
    },
    hasNewMessages: (channelId: number) =>
      get().socketData.newMessages.includes(channelId),
    hasChannelUpdate: (channelId: number) =>
      get().socketData.channelUpdates.includes(channelId),

    // UI state
    showEmojiPicker: false,
    toggleEmojiPicker: () =>
      set((state) => ({ showEmojiPicker: !state.showEmojiPicker })),
    closeEmojiPicker: () => set({ showEmojiPicker: false }),

    // Draft messages
    drafts: {},
    saveDraft: (channelId: number, text: string) =>
      set((state) => ({
        drafts: {
          ...state.drafts,
          [channelId]: text,
        },
      })),
    getDraft: (channelId: number) => get().drafts[channelId] || '',
    clearDraft: (channelId: number) =>
      set((state) => {
        const newDrafts = { ...state.drafts };
        delete newDrafts[channelId];
        return { drafts: newDrafts };
      }),
    hasDraft: (channelId: number) => {
      const draft = get().drafts[channelId];
      return !!draft && draft.trim().length > 0;
    },

    // Reply state
    replyingTo: {},
    setReplyingTo: (channelId: number, message: WhatsAppMessage | null) =>
      set((state) => ({
        replyingTo: {
          ...state.replyingTo,
          [channelId]: message,
        },
      })),
    clearReplyingTo: (channelId: number) =>
      set((state) => {
        const newReplyingTo = { ...state.replyingTo };
        delete newReplyingTo[channelId];
        return { replyingTo: newReplyingTo };
      }),

    // Editing state
    editingMessage: null,
    setEditingMessage: (message: WhatsAppMessage | null) =>
      set({ editingMessage: message }),
    clearEditingMessage: () => set({ editingMessage: null }),

    // Sounds - initialize from notificationSound saved state (inverted: muted = false, not muted = true)
    soundsEnabled: !notificationSound.getMuteState(),
    toggleSounds: () => {
      // Ensure audio context is enabled in the context of the user gesture
      try {
        notificationSound.enableAudio();
      } catch (e) {}
      notificationSound.toggleMute();
      set((state) => ({ soundsEnabled: !state.soundsEnabled }));
    },

    // Filter state
    showUnreadOnly: false,
    setShowUnreadOnly: (value: boolean) => set({ showUnreadOnly: value }),
    toggleUnreadFilter: () =>
      set((state) => ({ showUnreadOnly: !state.showUnreadOnly })),

    // Mutation tracking
    pendingMutations: new Map(),
    trackMutation: (messageId: number, mutation: PendingMutation) =>
      set((state) => {
        const newMap = new Map(state.pendingMutations);
        newMap.set(String(messageId), mutation);
        return { pendingMutations: newMap };
      }),
    clearMutation: (messageId: number) =>
      set((state) => {
        const newMap = new Map(state.pendingMutations);
        newMap.delete(String(messageId));
        return { pendingMutations: newMap };
      }),
    isPendingMutation: (messageId: number, maxAgeMs = 5000) => {
      const mutation = get().pendingMutations.get(String(messageId));
      if (!mutation) return false;
      const age = Date.now() - mutation.timestamp;
      return age < maxAgeMs;
    },
  }));
};

export const MessagingContext = createContext<MessagingStore | undefined>(
  undefined
);

// Module-level registered store reference so non-React modules (e.g. global socket)
// can update messaging state even when components are unmounted.
let __registeredMessagingStore: StoreApi<MessagingZustandStore> | undefined;

export const registerMessagingStore = (
  s: StoreApi<MessagingZustandStore> | undefined
) => {
  __registeredMessagingStore = s;
};

export const getRegisteredMessagingStore = () => __registeredMessagingStore;

export type MessagingContextProviderProps = {
  children: ReactNode;
};

export const MessagingContextProvider: React.FC<
  MessagingContextProviderProps
> = ({ children }) => {
  const storeRef = useRef<StoreApi<MessagingZustandStore>>();
  if (!storeRef.current) {
    storeRef.current = createMessagingStore();
    // Register the created store for external use (global socket, etc.)
    try {
      registerMessagingStore(storeRef.current);
    } catch (e) {
      // ignore
    }
  }
  const store = useStore(storeRef.current);

  return (
    <MessagingContext.Provider value={store}>
      {children}
    </MessagingContext.Provider>
  );
};

export const useMessaging = (): MessagingStore => {
  const context = useContext(MessagingContext);
  if (!context) {
    throw new Error(
      'useMessaging must be used within a MessagingContextProvider'
    );
  }
  return context;
};
